import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationType, Property } from '@app/shared/interfaces';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-property-availability-rules',
  templateUrl: './property-availability-rules.component.html',
  styleUrls: ['./property-availability-rules.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyAvailabilityRulesComponent {
  public loading$ = new BehaviorSubject<boolean>(true);
  private currentPropertyId$ = this.route.paramMap.pipe(
    map((params) => params.get('id')),
    tap((id) => this.propertyId$.next(id))
  );
  public propertyId$ = new BehaviorSubject<string | null>(null);
  public property$ = this.currentPropertyId$.pipe(
    switchMap((id) => this.propertyService.getProperty(id, ['can_manage_availability'])),
    map((res) => {
      if (res.error || !res.id) {
        this.toastErrorAndRedirect('Failed to load property.');
        return null;
      }

      if (!res.can_manage_availability) {
        this.toast.open(
          `Property availability rules for ${res.name} cannot be managed from Hospitable.`,
          'Dismiss',
          NotificationType.Error
        );
        this.router.navigate(['/properties/property', res.id]);
        return null;
      }
      return res as Property;
    }),
    tap(() => this.loading$.next(false)),
    shareReplay(1)
  );

  // Only include PMS scope properties in this list
  public properties$ = this.propertyService
    .getProperties({
      filterCriteria: [],
      paginate: false,
      transformer: 'simple',
      includes: ['can_manage_availability'],
    })
    .pipe(map((res) => res.data.filter((p) => p.can_manage_availability)));

  constructor(
    private readonly propertyService: PropertiesService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toast: ToastNotificationsService
  ) {}

  public propertyChanged(propertyId: number) {
    const url = this.router.url.split('/');
    const currentPath = url[url.length - 1];
    this.router.navigate([`/properties/property/${propertyId}`, currentPath]);
  }

  public navChanged({ path, heading }: { path: string; heading: string }): void {
    if (path === 'calendar') {
      this.router.navigate([`/calendar/property/${this.propertyId$.value}/`]);
      return;
    }
    this.router.navigate([`/properties/property/${this.propertyId$.value}/${path}`]);
  }

  private toastErrorAndRedirect(message): void {
    this.toast.error(message);
    this.router.navigate(['/properties']);
  }
}
